import React, { useState, useMemo } from 'react';
import { X, Mail, Users, User, Send, ChevronDown, ChevronRight, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

type Participant = {
  id: string;
  name: string;
  email: string | null;
  ean_code: string | null;
  groupe: string | null;
  type: string | null;
};

type InviteeEntry = {
  key: string;
  label: string;
  ean: string;
  email: string;
  isGroup: boolean;
};

type SendResult = {
  key: string;
  success: boolean;
  error?: string;
};

interface Props {
  participants: Participant[];
  onClose: () => void;
}

export function AccessInvitationModal({ participants, onClose }: Props) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<SendResult[]>([]);
  const [step, setStep] = useState<'select' | 'email' | 'done'>('select');

  const { grouped, ungrouped } = useMemo(() => {
    const g: Record<string, Participant[]> = {};
    const u: Participant[] = [];
    participants.forEach(p => {
      if (p.groupe) {
        if (!g[p.groupe]) g[p.groupe] = [];
        g[p.groupe].push(p);
      } else {
        u.push(p);
      }
    });
    return { grouped: g, ungrouped: u };
  }, [participants]);

  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleSelect = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectedInvitees: InviteeEntry[] = useMemo(() => {
    const entries: InviteeEntry[] = [];
    selectedKeys.forEach(key => {
      if (key.startsWith('group:')) {
        const groupName = key.slice(6);
        const members = grouped[groupName] || [];
        const ean = members.find(m => m.ean_code)?.ean_code || '';
        entries.push({ key, label: groupName, ean, email: emails[key] || '', isGroup: true });
      } else {
        const p = participants.find(x => x.id === key);
        if (p) {
          entries.push({ key, label: p.name, ean: p.ean_code || '', email: emails[key] || '', isGroup: false });
        }
      }
    });
    return entries;
  }, [selectedKeys, grouped, participants, emails]);

  const allEmailsFilled = selectedInvitees.length > 0 && selectedInvitees.every(i => i.email.trim() !== '' && i.email.includes('@'));

  const handleSend = async () => {
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const recipients = selectedInvitees.map(invitee => {
        const eanInfo = invitee.ean
          ? `Votre code EAN est : <strong>${invitee.ean}</strong>`
          : `Votre code EAN vous a été communiqué séparément.`;

        const message = `Bonjour,

Vous êtes invité(e) à accéder à la plateforme Sun Is Up de partage d'énergie.

Pour créer votre compte et accéder à vos données, veuillez suivre ces étapes :

<strong>1.</strong> Rendez-vous sur le site <a href="https://sunisup.be" style="color:#16a34a;">sunisup.be</a>

<strong>2.</strong> Cliquez sur <em>"Accès plateforme"</em>

<strong>3.</strong> Créez votre compte avec votre adresse e-mail

<strong>4.</strong> Lors de l'inscription, renseignez votre code EAN :
${eanInfo}

<strong>5.</strong> Votre compte sera activé après validation par notre équipe. Vous recevrez une confirmation par e-mail.

Si vous avez des questions, n'hésitez pas à nous contacter à <a href="mailto:info@sunisup.be" style="color:#16a34a;">info@sunisup.be</a>.

Bienvenue dans la communauté de partage d'énergie !`;

        return {
          name: invitee.label,
          email: invitee.email.trim(),
          subject: "Invitation d'accès à la plateforme Sun Is Up",
          message,
        };
      });

      const response = await fetch(`${supabaseUrl}/functions/v1/send-member-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipients }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'envoi');
      }

      const sendResults: SendResult[] = selectedInvitees.map((invitee, idx) => ({
        key: invitee.key,
        success: data.results?.[idx]?.success ?? false,
        error: data.results?.[idx]?.error,
      }));

      setResults(sendResults);
      setStep('done');

      const successCount = sendResults.filter(r => r.success).length;
      if (successCount === sendResults.length) {
        toast.success(`${successCount} invitation(s) envoyée(s) avec succès`);
      } else {
        toast.error(`${successCount}/${sendResults.length} invitation(s) envoyée(s)`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-neutral-900">Invitation d'accès</h2>
              <p className="text-xs text-neutral-500">Invitez des participants à rejoindre la plateforme</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center px-6 py-3 bg-neutral-50 border-b border-neutral-100">
          {['Sélection', 'Adresses e-mail', 'Envoi'].map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center space-x-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                  (step === 'select' && i === 0) || (step === 'email' && i === 1) || (step === 'done' && i === 2)
                    ? 'bg-green-600 text-white'
                    : (step === 'email' && i === 0) || (step === 'done' && i <= 1)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-neutral-200 text-neutral-500'
                }`}>
                  {((step === 'email' && i === 0) || (step === 'done' && i <= 1)) ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : i + 1}
                </div>
                <span className={`text-xs font-medium ${
                  (step === 'select' && i === 0) || (step === 'email' && i === 1) || (step === 'done' && i === 2)
                    ? 'text-green-700'
                    : 'text-neutral-400'
                }`}>{s}</span>
              </div>
              {i < 2 && <div className="flex-1 h-px bg-neutral-200 mx-3" />}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Step 1: Selection */}
          {step === 'select' && (
            <div>
              <p className="text-sm text-neutral-600 mb-4">
                Sélectionnez les participants ou groupes à inviter.
              </p>
              <div className="space-y-1">
                {/* Groups */}
                {Object.entries(grouped).map(([groupName, members]) => (
                  <div key={`group-${groupName}`}>
                    <div className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                      selectedKeys.has(`group:${groupName}`) ? 'bg-green-50 border border-green-200' : 'hover:bg-neutral-50 border border-transparent'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(`group:${groupName}`)}
                        onChange={() => toggleSelect(`group:${groupName}`)}
                        className="w-4 h-4 rounded text-green-600 accent-green-600"
                        onClick={e => e.stopPropagation()}
                      />
                      <button
                        className="flex items-center space-x-2 flex-1 text-left"
                        onClick={() => toggleGroup(groupName)}
                      >
                        <Users className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-neutral-900">{groupName}</span>
                          <span className="ml-2 text-xs text-neutral-500">{members.length} membre(s)</span>
                        </div>
                        {expandedGroups.has(groupName) ? (
                          <ChevronDown className="w-4 h-4 text-neutral-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-neutral-400" />
                        )}
                      </button>
                    </div>
                    {expandedGroups.has(groupName) && (
                      <div className="ml-10 space-y-1 mt-1">
                        {members.map(m => (
                          <div key={m.id} className="flex items-center space-x-2 px-3 py-1.5 text-xs text-neutral-600 bg-neutral-50 rounded">
                            <User className="w-3 h-3 text-neutral-400" />
                            <span>{m.name}</span>
                            {m.ean_code && <span className="text-neutral-400 font-mono">{m.ean_code}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Ungrouped participants */}
                {ungrouped.map(p => (
                  <div
                    key={p.id}
                    className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                      selectedKeys.has(p.id) ? 'bg-green-50 border border-green-200' : 'hover:bg-neutral-50 border border-transparent'
                    }`}
                    onClick={() => toggleSelect(p.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedKeys.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="w-4 h-4 rounded text-green-600 accent-green-600"
                      onClick={e => e.stopPropagation()}
                    />
                    <User className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-neutral-900">{p.name}</span>
                      {p.ean_code && (
                        <span className="ml-2 text-xs text-neutral-400 font-mono">{p.ean_code}</span>
                      )}
                    </div>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      p.type === 'producer' ? 'bg-amber-100 text-amber-700'
                      : p.type === 'prosumer' ? 'bg-orange-100 text-orange-700'
                      : 'bg-green-100 text-green-700'
                    }`}>
                      {p.type === 'producer' ? 'Producteur' : p.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}
                    </span>
                  </div>
                ))}

                {participants.length === 0 && (
                  <p className="text-sm text-neutral-400 text-center py-8">Aucun participant disponible</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Email addresses */}
          {step === 'email' && (
            <div>
              <p className="text-sm text-neutral-600 mb-4">
                Renseignez l'adresse e-mail pour chaque destinataire. Un e-mail d'invitation sera envoyé avec les instructions de création de compte.
              </p>
              <div className="space-y-3">
                {selectedInvitees.map(invitee => (
                  <div key={invitee.key} className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                    <div className="flex items-center space-x-2 mb-3">
                      {invitee.isGroup ? (
                        <Users className="w-4 h-4 text-amber-600" />
                      ) : (
                        <User className="w-4 h-4 text-neutral-500" />
                      )}
                      <span className="text-sm font-medium text-neutral-900">{invitee.label}</span>
                      {invitee.ean && (
                        <span className="text-xs text-neutral-400 font-mono bg-white px-2 py-0.5 rounded border border-neutral-200">
                          EAN: {invitee.ean}
                        </span>
                      )}
                    </div>
                    <input
                      type="email"
                      placeholder="adresse@email.com"
                      value={invitee.email}
                      onChange={e => setEmails(prev => ({ ...prev, [invitee.key]: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 'done' && (
            <div>
              <div className="text-center mb-6">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  results.every(r => r.success) ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  {results.every(r => r.success) ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <AlertCircle className="w-8 h-8 text-amber-600" />
                  )}
                </div>
                <h3 className="text-base font-semibold text-neutral-900">
                  {results.filter(r => r.success).length}/{results.length} invitation(s) envoyée(s)
                </h3>
              </div>
              <div className="space-y-2">
                {results.map((result, idx) => {
                  const invitee = selectedInvitees[idx];
                  return (
                    <div key={result.key} className={`flex items-center space-x-3 px-4 py-3 rounded-lg ${
                      result.success ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
                    }`}>
                      {result.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900">{invitee?.label}</p>
                        <p className="text-xs text-neutral-500 truncate">{invitee?.email}</p>
                        {result.error && <p className="text-xs text-red-600 mt-0.5">{result.error}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <div className="text-xs text-neutral-500">
            {step === 'select' && selectedKeys.size > 0 && `${selectedKeys.size} sélectionné(s)`}
            {step === 'email' && `${selectedInvitees.length} destinataire(s)`}
          </div>
          <div className="flex items-center space-x-3">
            {step === 'done' ? (
              <button
                onClick={onClose}
                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                Fermer
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    if (step === 'email') setStep('select');
                    else onClose();
                  }}
                  className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  {step === 'email' ? 'Retour' : 'Annuler'}
                </button>
                {step === 'select' && (
                  <button
                    onClick={() => setStep('email')}
                    disabled={selectedKeys.size === 0}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                )}
                {step === 'email' && (
                  <button
                    onClick={handleSend}
                    disabled={!allEmailsFilled || sending}
                    className="inline-flex items-center px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer les invitations
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
